/**
 * useSearch — custom hook with 300ms debounce, AbortController cancellation,
 * minimum 2-char threshold, and timeout handling.
 *
 * Adapted for the frontend project (no real API yet — uses mock search).
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import type { GroupedSearchResults, SearchError } from './types';
import { realSearch } from './search-service';
import { useActiveSeason } from '@/hooks/useActiveSeason';

export interface UseSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  results: GroupedSearchResults | null;
  isLoading: boolean;
  error: SearchError | null;
  retry: () => void;
}

export function useSearch(): UseSearchReturn {
  const [query, setQueryState] = useState('');
  const [results, setResults] = useState<GroupedSearchResults | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<SearchError | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef('');

  // Season SSOT: resolve the operative competition year through the
  // active-season seam and pass it to the (non-React) search service, which
  // never re-derives the year itself. Held in a ref so the debounced
  // callbacks read the latest value without re-binding on every resolution.
  const { operativeYear } = useActiveSeason();
  const operativeYearRef = useRef(operativeYear);
  operativeYearRef.current = operativeYear;

  const executeSearch = useCallback(async (searchQuery: string) => {
    setIsLoading(true);
    setError(null);

    try {
      // Query real data sources (GraphQL + localStorage cache)
      const searchResults = await realSearch(searchQuery, operativeYearRef.current);
      setResults(searchResults);
      setIsLoading(false);
    } catch {
      setError({ type: 'network', message: 'Search failed. Please try again.' });
      setResults(null);
      setIsLoading(false);
    }
  }, []);

  const setQuery = useCallback(
    (newQuery: string) => {
      setQueryState(newQuery);
      lastQueryRef.current = newQuery;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      if (newQuery.length < 2) {
        setResults(null);
        setIsLoading(false);
        setError(null);
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        executeSearch(newQuery);
      }, 300);
    },
    [executeSearch],
  );

  const retry = useCallback(() => {
    if (lastQueryRef.current.length >= 2) {
      executeSearch(lastQueryRef.current);
    }
  }, [executeSearch]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return { query, setQuery, results, isLoading, error, retry };
}
